import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban } from 'lucide-react';
import { formatIqd, fromMillidinars, voidSessionSchema, type SessionDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * The sitting a void is aimed at, whichever screen it came from — the tile's
 * running sitting or a finished, unpaid one in the device's Outstanding list.
 */
export interface VoidTarget {
  sessionId: string;
  deviceName: string;
  /** The party's name, as the tab reads it. */
  partyName: string;
  /** Running (or held) frees the machine; finished only comes off the bill. */
  state: 'running' | 'finished';
  /** What it would have charged, millidinars — the money the void gives up. */
  amountMd: string | null;
}

/**
 * Void a sitting with a reason — SPEC §5.3, the door for
 * `POST /sessions/:id/void` (`sessions.void`).
 *
 * The reason is required, and the dialog says where it goes: the audit log,
 * under the name of whoever pressed the button. That sentence is the control
 * — a void is the one way play leaves the system without money, and the
 * person doing it should know it is being written down.
 *
 * Only ever opened for holders of `sessions.void`; the server refuses
 * everyone else regardless, and the refusal is shown in words.
 */
export function VoidSessionDialog({
  target,
  currencySymbol,
  onClose,
  onDone,
}: {
  target: VoidTarget;
  currencySymbol: string;
  onClose: () => void;
  onDone: (session: SessionDto) => void;
}) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The same schema the route validates with, so the button and the server
  // cannot disagree about what counts as a reason.
  const valid = voidSessionSchema.safeParse({ reason }).success;

  async function handleConfirm(): Promise<void> {
    setTouched(true);
    if (!valid) return;
    setError(null);
    setSubmitting(true);
    try {
      const session = await api.post<SessionDto>(`/sessions/${target.sessionId}/void`, {
        reason: reason.trim(),
      });
      onDone(session);
      onClose();
    } catch (caught) {
      setError(voidFailureMessage(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  const fieldClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary';

  const title = t('sessionVoid.title', { device: target.deviceName });

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-sm rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-content">
          <Ban className="h-5 w-5 shrink-0 text-danger" aria-hidden />
          {title}
        </h2>

        <p className="mt-2 text-sm text-content-secondary">
          {t(
            target.state === 'running'
              ? 'sessionVoid.runningMessage'
              : 'sessionVoid.finishedMessage',
            {
              party: target.partyName,
              device: target.deviceName,
            },
          )}
        </p>
        {target.amountMd !== null && fromMillidinars(target.amountMd) > 0n ? (
          <p className="mt-1 text-sm text-content-secondary" data-void-worth>
            {t('sessionVoid.worth', {
              amount: formatIqd(fromMillidinars(target.amountMd), {
                currencySymbol,
                locale: i18n.language,
              }),
            })}
          </p>
        ) : null}

        <label className="mt-4 block">
          <span className="mb-1 block text-xs text-content-secondary">
            {t('sessionVoid.reason')}
          </span>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            maxLength={300}
            rows={2}
            autoFocus
            required
            aria-invalid={touched && !valid}
            className={fieldClass}
          />
          <span className="mt-1 block text-xs text-content-muted">
            {t('sessionVoid.reasonHint')}
          </span>
          {touched && !valid ? (
            <span className="mt-1 block text-xs text-danger">
              {t('sessionVoid.reasonTooShort')}
            </span>
          ) : null}
        </label>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            data-action="confirm-void"
            onClick={() => void handleConfirm()}
            disabled={submitting || !valid}
            className="rounded-full bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-danger-hover disabled:opacity-60"
          >
            {submitting ? t('sessionVoid.voiding') : t('sessionVoid.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The refusal in words. The server's own message is already written for
 * staff (`session_already_billed` names the bill to void instead), so it is
 * shown as is; only a missing permission gets the app's own sentence, which
 * names the privilege the way the Employees screen does.
 */
export function voidFailureMessage(caught: unknown, t: (key: string) => string): string {
  if (caught instanceof ApiError) {
    if (caught.status === 403) return t('sessionVoid.noPermission');
    if (caught.message) return caught.message;
  }
  return t('sessionVoid.failed');
}
